import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal, Space } from "antd";
import { ThumbsUp, ThumbsDown, MessageCircle } from "lucide-react";
import { showApiError } from "@/utils";
import "./feedback.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SectionFeedback = ({ section, isFrozen }) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null); // like | dislike | comment

  const sectionDisplayNames = {
    genai_queries: t("contentOptimizer.promptsHeading", "GenAI Prompts"),
    closest_urls: t("contentOptimizer.pagesHeading", "Closest URLs"),
    related_queries: t("contentOptimizer.relatedQueries", "Related Prompts"),
    top_competitor_pages: t("contentOptimizer.topCompetitorPages", "Top Performing Competitor Pages"),
    top_web_sources: t("contentOptimizer.topWebSources", "Top Web Sources"),
  };

  const handleLike = () => {
    setSelectedFeedback("like");
    handleSubmitFeedback("Positive");
  };

  const handleDislike = () => {
    setSelectedFeedback("dislike");
    handleSubmitFeedback("Negative");
  };

  const handleCommentChange = (e) => {
    setComment(e.target.value);
  };

  const handleSubmitComment = () => {
    setSelectedFeedback("comment");
    handleSubmitFeedback(comment.trim());
  };

  const handleSubmitFeedback = (impression) => {
    const feedback = {
      section,
      impression,
    };

    fetch(`${BACKEND_URL}/api/v1/optimize_content/overall-feedback`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ feedbacks: [feedback] }),
    })
      .then((res) => {
        if (!res.ok) {
          console.error(`Error submitting feedback: ${res.statusText}`);
          showApiError(
            t(
              "contentOptimizer.submitFeedbackError",
              "Failed to submit feedback. Please try again.",
            ),
          );
        }
      })
      .catch((err) => {
        console.error("Error submitting feedback:", err);
        showApiError(
          t(
            "contentOptimizer.submitFeedbackError",
            "Failed to submit feedback. Please try again.",
          ),
        );
      })
      .finally(() => {
        setShowCommentModal(false);
        setComment("");
      });
  };

  return (
    <div className="feedback-bar" data-testid={`section-feedback-${section}`}>
      <Space size={4} className="feedback-bar-space">
        <Button
          data-testid={`section-feedback-like-${section}`}
          icon={
            <ThumbsUp
              color={
                selectedFeedback === "like" ? "var(--brand-color-1)" : undefined
              }
              size={20}
              strokeWidth={2}
            />
          }
          onClick={handleLike}
          type="text"
          className={`feedback-bar-btn${selectedFeedback === "like" ? " feedback-bar-btn-active" : ""}`}
          disabled={isFrozen}
        />
        <Button
          data-testid={`section-feedback-dislike-${section}`}
          icon={
            <ThumbsDown
              color={
                selectedFeedback === "dislike" ? "var(--error)" : undefined
              }
              size={20}
              strokeWidth={2}
            />
          }
          onClick={handleDislike}
          type="text"
          className={`feedback-bar-btn${selectedFeedback === "dislike" ? " feedback-bar-btn-active" : ""}`}
          disabled={isFrozen}
        />
        <Button
          data-testid={`section-feedback-comment-${section}`}
          icon={
            <MessageCircle
              color={
                selectedFeedback === "comment" ? "var(--warning)" : undefined
              }
              size={20}
              strokeWidth={2}
            />
          }
          onClick={() => !isFrozen && setShowCommentModal(true)}
          type="text"
          className={`feedback-bar-btn${selectedFeedback === "comment" ? " feedback-bar-btn-active" : ""}`}
          disabled={isFrozen}
        />
      </Space>
      <Modal
        title={t(
          "contentOptimizer.sectionQuestion",
          `What do you think about the "${sectionDisplayNames[section] || section}" section?`,
          { section: sectionDisplayNames[section] || section },
        )}
        open={showCommentModal}
        onCancel={() => setShowCommentModal(false)}
        footer={[
          <Button key="Cancel" onClick={() => setShowCommentModal(false)}>
            {t("common.cancel", "Cancel")}
          </Button>,
          <Button
            key="Submit"
            onClick={handleSubmitComment}
            disabled={!comment.trim()}
            style={{
              backgroundColor: "var(--brand-color-1)",
              color: "#ffffff",
              border: "none",
            }}
          >
            {t("common.submit", "Submit")}
          </Button>,
        ]}
        zIndex={9999}
        maskClosable={!isFrozen}
        closable={!isFrozen}
      >
        <textarea
          className="comment-modal-textarea"
          value={comment}
          onChange={handleCommentChange}
          placeholder={t("contentOptimizer.leaveComment", "Leave a comment")}
          disabled={isFrozen}
        />
      </Modal>
    </div>
  );
};

export default SectionFeedback;
